import { isStagingSession, selectStagingSession, stagingDatabaseUrl, stagingAuth } from './staging';
import { requestNativeDatabaseWrite } from './features/schedule-sync/nativeWrite';
import FeatureTour from './features/feature-tour/FeatureTour';
import { FeatureTourPreviewRole, getFeatureTourPreviewRole } from './features/feature-tour/previewMode';
import { useFeatureTour } from './features/feature-tour/useFeatureTour';
import { useAppUpdatePromptOpen } from './features/app-update/AppUpdateProvider';
import { canUseWebsite } from './features/auth/webAccess';
import { chooseAutoTvSchedule, chooseTvSchedule } from './features/tv/tvSchedules';
import TvScheduleControls from './features/tv/TvScheduleControls';
// src/App.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Login from './components/Login';
import Calendar from './components/Calendar';
import DailyView from './components/DailyView';
import AcademyScheduleImportModal from './features/schedule-import/AcademyScheduleImportModal';
import AcademySwitcher from './shared/components/AcademySwitcher';
import ScheduleNotificationModal, { PendingScheduleNotification } from './components/ScheduleNotificationModal';
import GeneralSettings from './components/GeneralSettings';
import NotificationOnboarding from './components/NotificationOnboarding';
import NotificationCenter from './features/notification-center/NotificationCenter';
import { getHistoryCredential, setHistoryCredential } from './features/notification-center/api';
import TestSessionControls from './features/test-session/TestSessionControls';
import { activeBLCStudentCycles, canReadCrossAcademySchedules, canShowCrossAcademyConflicts, createTestAccessProfile, TestViewId } from './features/test-session/testAccessProfiles';
import { DailySchedule, UserRole, TrainingEvent } from './types/schedule';
import { AccessProfile, AcademyId, hasPermission } from './types/academy';
import { getAcademyConfig } from './config/academies';
import { normalizeAccessCode, resolveAccessCode, restoreSavedAccessProfile } from './features/auth/accessCodes';
import { assertTestSessionWriteAllowed } from './features/auth/testModePolicy';
import { EventSearchResult } from './features/event-search/searchEvents';
import type { CrossAcademyConflictCheckStatus, CrossAcademyConflictNotice } from './features/schedule-conflicts/notices';
import NcoaChatbot from './features/chatbot/NcoaChatbot';
import {
  isAppOpenAdShowing,
  showAppOpenAdIfEligible
} from './features/ads/appOpen';
import { mockSchedules } from './data/mockData';
import { auth, db, getDatabaseRestUrl, useFirebaseEmulators } from './firebase';
import { ref, onValue, set, update, remove } from 'firebase/database';
import { signInAnonymously, signOut } from 'firebase/auth';
import { waitForLogoutCleanup } from './features/auth/logoutCleanup';
import { Capacitor } from '@capacitor/core';
import {
  clearAdminSessionToken,
  createAdminSession,
  prepareNotificationEnvironment,
  disableNotifications,
  getAdminIdToken,
  isPhoneDevice,
  listenForForegroundNotifications,
  getCrossAcademyConflictNotices
} from './notifications';

const LEGACY_06_26_START = '2026-04-20';
const LEGACY_06_26_END = '2026-05-15';
const LEGACY_06_26_CYCLE = '06-26';
const LOGIN_STORAGE_KEY = 'blc_calendar_login';
const DISPLAY_MODE_STORAGE_KEY = 'blc_calendar_display_mode';
const DARK_MODE_STORAGE_KEY = 'blc_calendar_dark_mode';

export type DisplayMode = 'auto' | 'tv';

type SavedLogin = {
  code?: string;
  role?: UserRole;
  testMode?: boolean;
  profile?: AccessProfile;
};

type NotificationFocus = {
  academy?: AcademyId;
  date: string;
  targetId: string;
  changeType: string;
  previewText?: string;
  changedFields: string[];
};

type ForegroundNotification = NotificationFocus & {
  id: number;
};

const getNotificationFocusFromUrl = (): NotificationFocus | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const date = params.get('date');
  const targetId = params.get('highlight');
  if (!date || !targetId) return null;
  return {
    date,
    targetId,
    changeType: params.get('change') || 'Schedule updated',
    previewText: params.get('preview') || undefined,
    changedFields: (params.get('fields') || '').split(',').filter(Boolean)
  };
};

const getLocalTodayString = (today = new Date()) => {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const getScheduleEndDateTime = (schedule?: DailySchedule | null) => {
  if (!schedule?.events?.length) return null;

  const latestEndTime = schedule.events.reduce<string | null>((latest, event) => {
    const [, rawEndTime] = event.time.split('-');
    const normalizedEndTime = rawEndTime?.trim().padStart(4, '0');
    if (!normalizedEndTime || !/^\d{4}$/.test(normalizedEndTime)) return latest;
    return !latest || normalizedEndTime > latest ? normalizedEndTime : latest;
  }, null);

  if (!latestEndTime) return null;

  const [year, month, day] = schedule.date.split('-').map(Number);
  if (!year || !month || !day) return null;

  const endDate = new Date(year, month - 1, day);
  endDate.setHours(
    Number(latestEndTime.slice(0, 2)),
    Number(latestEndTime.slice(2, 4)),
    0,
    0
  );
  return endDate;
};

const updateDatabaseValues = async (updates: Record<string, unknown>, readOnlyTestSession = false) => {
  assertTestSessionWriteAllowed(readOnlyTestSession, isStagingSession());
  if (!Capacitor.isNativePlatform() && !isStagingSession()) {
    await update(ref(db), updates);
    return;
  }

  const restUpdates = Object.fromEntries(
    Object.entries(updates).map(([path, value]) => [path.replace(/^\/+/, ''), value])
  );
  await requestNativeDatabaseWrite('', 'PATCH', restUpdates);
};

const setDatabaseValue = async (path: string, value: unknown, readOnlyTestSession = false) => {
  assertTestSessionWriteAllowed(readOnlyTestSession, isStagingSession());
  if (Capacitor.isNativePlatform() || isStagingSession()) {
    await requestNativeDatabaseWrite(path, 'PUT', value);
    return;
  }
  await set(ref(db, path), value);
};

const removeDatabaseValue = async (path: string, readOnlyTestSession = false) => {
  assertTestSessionWriteAllowed(readOnlyTestSession, isStagingSession());
  if (Capacitor.isNativePlatform() || isStagingSession()) {
    await requestNativeDatabaseWrite(path, 'DELETE');
    return;
  }
  await remove(ref(db, path));
};

const truncateNotificationPreview = (value: string, maxLength = 90) => {
  const compact = value.replace(/\s+/g, ' ').trim();
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, maxLength - 1)}…`;
};

const buildEventPreview = (event: TrainingEvent, fields: string[]) => {
  if (fields.length === 0) return truncateNotificationPreview(event.eventName || 'Event updated');

  const labels: Record<string, string> = {
    time: 'TIME',
    eventName: 'EVENT',
    location: 'LOC',
    uniform: 'UNI',
    highlighted: 'HIGHLIGHT'
  };
  const values: Record<string, string> = {
    time: event.time,
    eventName: event.eventName,
    location: event.location,
    uniform: event.uniform,
    highlighted: event.highlighted ? 'ON' : 'OFF'
  };

  return truncateNotificationPreview(
    fields.map(field => `${labels[field] || field}: ${values[field] || ''}`).join(' · ')
  );
};

function AppApplication() {
  const getSavedProfile = (): AccessProfile | null => {
    if (typeof window === 'undefined') return null;
    try {
      const parsed = JSON.parse(window.localStorage.getItem(LOGIN_STORAGE_KEY) || 'null') as SavedLogin | null;
      const savedProfile = parsed?.profile || null;
      const profile = restoreSavedAccessProfile(savedProfile, parsed?.testMode === true);
      if (!Capacitor.isNativePlatform() && parsed?.testMode !== true && !canUseWebsite(profile)) return null;
      return profile;
    } catch {
      return null;
    }
  };
  const [accessProfile, setAccessProfile] = useState<AccessProfile | null>(getSavedProfile);
  const [academy, setAcademy] = useState<AcademyId>(() => getSavedProfile()?.academy || 'BLC');
  const [role, setRole] = useState<UserRole>(() => {
    if (typeof window === 'undefined') return null;

    try {
      const saved = window.localStorage.getItem(LOGIN_STORAGE_KEY);
      if (!saved) return null;

      const parsed = JSON.parse(saved) as SavedLogin | null;
      if (!Capacitor.isNativePlatform()) return getSavedProfile()?.role || null;
      if (parsed?.profile?.role) return parsed.profile.role;
      if (parsed?.role === 'ADMIN') return 'ADMIN';
      if (!parsed?.code) return null;
      if (parsed.role === 'VIEWER') return parsed.role;
      return null;
    } catch {
      return null;
    }
  });
  const [isTestMode, setIsTestMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const saved = JSON.parse(window.localStorage.getItem(LOGIN_STORAGE_KEY) || 'null') as SavedLogin | null;
      return saved?.role === 'ADMIN' && saved.testMode === true;
    } catch {
      return false;
    }
  });
  const [testViewId, setTestViewId] = useState<TestViewId>('test-manager');
  const [testStudentCycles, setTestStudentCycles] = useState<string[]>([]);
  const [schedules, setSchedules] = useState<DailySchedule[]>([]);
  const [crossAcademySchedules, setCrossAcademySchedules] = useState<DailySchedule[]>([]);
  const [crossAcademyConflictNotices, setCrossAcademyConflictNotices] = useState<CrossAcademyConflictNotice[]>([]);
  const [crossAcademyConflictCheckStatus, setCrossAcademyConflictCheckStatus] = useState<CrossAcademyConflictCheckStatus>('idle');
  const [locations, setLocations] = useState<string[]>([]);
  const [uniforms, setUniforms] = useState<string[]>([]);
  const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
  const [tvCycle, setTvCycle] = useState<string | null>(null);
  const [tvNow, setTvNow] = useState(Date.now);
  const isTvDisplay = accessProfile?.accessLevel === 'TV_DISPLAY';
  const scheduleConflictRequestSignature = useMemo(
    () => JSON.stringify(schedules.map(schedule => ({
      date: schedule.date,
      events: (schedule.events || []).map(event => [event.id, event.time, event.location])
    }))),
    [schedules]
  );

  useEffect(() => {
    if (!isTvDisplay) return;
    const refresh = () => setTvNow(Date.now());
    refresh();
    const timer = window.setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [isTvDisplay]);
  const [studentCycleName, setStudentCycleName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [pendingNotification, setPendingNotification] = useState<PendingScheduleNotification | null>(null);
  const [notificationFocus, setNotificationFocus] = useState<NotificationFocus | null>(getNotificationFocusFromUrl);
  const [foregroundNotification, setForegroundNotification] = useState<ForegroundNotification | null>(null);
  const [isTrackingAuthorizationResolved, setIsTrackingAuthorizationResolved] = useState(
    () => Capacitor.getPlatform() !== 'ios'
  );
  const [notificationOnboardingComplete, setNotificationOnboardingComplete] = useState(() =>
    typeof window !== 'undefined' && window.localStorage.getItem('blc_notification_onboarding_dismissed') === 'true'
  );
  const updatePromptOpen = useAppUpdatePromptOpen();
  const featureTour = useFeatureTour(Boolean(role && accessProfile && !isTvDisplay),
    isLoading || (Boolean(apiError) && schedules.length === 0) || updatePromptOpen || isImportModalOpen || Boolean(pendingNotification));
  const tourShownThisSession = useRef(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('auto');
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true';
  });
  const hasAutoSelectedTodayRef = useRef(false);
  const logoutCleanupRef = useRef<Promise<unknown>>(Promise.resolve());
  const scheduleDatabaseKeyByDateRef = useRef<Map<string, string>>(new Map());
  const eventDatabaseKeysRef = useRef(new Map<string, Map<string, string>>());
  const recordScheduleKeys = (entries: Array<[string, DailySchedule]>) => {
    const days = entries.filter(([, day]) => Boolean(day?.date));
    scheduleDatabaseKeyByDateRef.current = new Map(days.map(([key, day]) => [day.date, key]));
    eventDatabaseKeysRef.current = new Map(days.map(([, day]) => {
      const keys = new Map<string, string>();
      Object.entries(day.events || {}).forEach(([key, event]) => {
        if (event?.id && !keys.has(event.id)) keys.set(event.id, key);
      });
      return [day.date, keys];
    }));
  };

  const normalizeScheduleEvents = (events: TrainingEvent[] | Record<string, TrainingEvent> | undefined) => {
    const values = Array.isArray(events) ? events : Object.values(events || {});
    const seenIds = new Set<string>();
    return values.filter(event => {
      if (!event?.id || seenIds.has(event.id)) return false;
      seenIds.add(event.id);
      return true;
    }).map(event => ({ ...event, highlighted: Boolean(event.highlighted) }));
  };

  const getScheduleDatabaseKey = (dateStr: string) =>
    scheduleDatabaseKeyByDateRef.current.get(dateStr);
  // Academy paths are identical in shape, but staging uses a separate project.
  const getDatabasePath = (path: string) =>
    `${getAcademyConfig(academy).databasePrefix}${path.replace(/^\/+/, '')}`;
  const getScheduleUpdatePath = (path: string) =>
    `/${getDatabasePath(path.replace(/^\/+/, ''))}`;

  const handleDarkModeChange = (enabled: boolean) => {
    setDarkMode(enabled);
    window.localStorage.setItem(DARK_MODE_STORAGE_KEY, String(enabled));
  };

  useEffect(() => {
    window.localStorage.removeItem(DISPLAY_MODE_STORAGE_KEY);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('theme-dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    // TV profiles always use the TV layout; other profiles keep their preference.
    if (isTvDisplay && displayMode !== 'tv') {
      setDisplayMode('tv');
    }
  }, [isTvDisplay, displayMode]);

  useEffect(() => {
    const handleTrackingAuthorizationResolved = () => setIsTrackingAuthorizationResolved(true);
    window.addEventListener('blc-att-resolved', handleTrackingAuthorizationResolved);
    return () => window.removeEventListener('blc-att-resolved', handleTrackingAuthorizationResolved);
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    listenForForegroundNotifications().then(listener => {
      unsubscribe = listener;
    }).catch(console.error);
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    const handleForegroundNotification = (event: Event) => {
      const detail = (event as CustomEvent<NotificationFocus>).detail;
      if (!detail?.date || !detail.targetId) return;
      if (!accessProfile) return;
      const notificationAcademy = detail.academy || 'BLC';
      if (accessProfile.accessLevel !== 'NCOA_MANAGER' && notificationAcademy !== accessProfile.academy) return;

      hasAutoSelectedTodayRef.current = true;
      setSelectedDateId(detail.date);
      setNotificationFocus(null);
      window.setTimeout(() => setNotificationFocus(detail), 0);
      setForegroundNotification({ ...detail, id: Date.now() });
    };

    window.addEventListener('blc-schedule-notification', handleForegroundNotification);
    return () => window.removeEventListener('blc-schedule-notification', handleForegroundNotification);
  }, [accessProfile]);

  useEffect(() => {
    if (!foregroundNotification) return;
    const clearTimer = window.setTimeout(() => setForegroundNotification(null), 8000);
    return () => window.clearTimeout(clearTimer);
  }, [foregroundNotification]);

  useEffect(() => {
    const canReadOtherAcademy = canReadCrossAcademySchedules(accessProfile?.accessLevel, isTestMode);
    if (!canReadOtherAcademy) {
      setCrossAcademySchedules([]);
      return;
    }
    let disposed = false;
    const otherAcademy: AcademyId = academy === 'BLC' ? 'KTA' : 'BLC';
    const path = `${getAcademyConfig(otherAcademy).databasePrefix}schedules`;
    const loadOtherAcademy = async () => {
      try {
        let url: string;
        if (isTestMode) {
          if (!isStagingSession()) return;
          const token = await getAdminIdToken();
          if (!token) return;
          url = stagingDatabaseUrl(path, token);
        } else {
          url = getDatabaseRestUrl(path);
        }
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) throw new Error(`Cross-academy schedule request failed (${response.status})`);
        const values = await response.json();
        const entries = values && typeof values === 'object' ? Object.values(values) as DailySchedule[] : [];
        if (!disposed) setCrossAcademySchedules(entries
          .filter(day => Boolean(day?.date))
          .map(day => ({ ...day, academy: otherAcademy, events: normalizeScheduleEvents(day.events) })));
      } catch (error) {
        if (!disposed) {
          console.error('Could not load schedules for commander location-conflict check:', error);
          setCrossAcademySchedules([]);
        }
      }
    };
    void loadOtherAcademy();
    const timer = window.setInterval(() => void loadOtherAcademy(), 30000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [accessProfile?.accessLevel, academy, isTestMode]);

  useEffect(() => {
    const isScopedAcademyChief = role === 'ADMIN'
      && accessProfile?.accessLevel === 'SCHEDULE_IMPORTER'
      && accessProfile.scope === academy;
    const isNcoaManager = accessProfile?.accessLevel === 'NCOA_MANAGER'
      && accessProfile.scope === 'NCOA';
    const isScopedSgl = accessProfile?.accessLevel === 'SENIOR'
      && accessProfile.scope === academy;
    if ((!isScopedAcademyChief && !isNcoaManager && !isScopedSgl) || isTestMode) {
      setCrossAcademyConflictNotices([]);
      setCrossAcademyConflictCheckStatus('idle');
      return;
    }

    let disposed = false;
    const scheduleConflictInputs = JSON.parse(scheduleConflictRequestSignature) as Array<{ date: string }>;
    const dates = Array.from(new Set(scheduleConflictInputs.map(schedule => schedule.date).filter(Boolean))).sort();
    if (dates.length === 0) {
      setCrossAcademyConflictNotices([]);
      setCrossAcademyConflictCheckStatus('ready');
      return;
    }

    let isChecking = false;
    const checkConflicts = async () => {
      if (isChecking || disposed) return;
      isChecking = true;
      try {
        const readerCode = isScopedSgl ? getHistoryCredential() : null;
        if (isScopedSgl && !readerCode) throw new Error('Sign in again to verify shared-location conflicts.');
        const notices = await getCrossAcademyConflictNotices(academy, dates, readerCode);
        if (disposed) return;
        setCrossAcademyConflictNotices(notices);
        setCrossAcademyConflictCheckStatus('ready');
      } catch (error) {
        if (disposed) return;
        console.error('Cross-academy location conflict check failed:', error);
        setCrossAcademyConflictNotices([]);
        setCrossAcademyConflictCheckStatus('unavailable');
      } finally {
        isChecking = false;
      }
    };

    setCrossAcademyConflictCheckStatus('checking');
    void checkConflicts();
    const timer = window.setInterval(() => void checkConflicts(), 30000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [accessProfile?.accessLevel, accessProfile?.scope, academy, isTestMode, role, scheduleConflictRequestSignature]);

  useEffect(() => {
    if (!isTestMode) {
      setTestStudentCycles([]);
      return;
    }
    let disposed = false;
    const loadBLCStudentCycles = async () => {
      try {
        if (!isStagingSession()) return;
        const token = await getAdminIdToken();
        if (!token) return;
        const path = `${getAcademyConfig('BLC').databasePrefix}schedules`;
        const response = await fetch(stagingDatabaseUrl(path, token), { cache: 'no-store' });
        if (!response.ok) return;
        const value = await response.json();
        const entries = Array.isArray(value) ? value : value && typeof value === 'object'
          ? Object.values(value as Record<string, DailySchedule>)
          : [];
        if (!disposed) setTestStudentCycles(activeBLCStudentCycles(entries as DailySchedule[]));
      } catch {
        if (!disposed) setTestStudentCycles([]);
      }
    };
    void loadBLCStudentCycles();
    return () => { disposed = true; };
  }, [isTestMode]);

  useEffect(() => {
    if (!notificationFocus || selectedDateId !== notificationFocus.date) return;
    const clearTimer = window.setTimeout(() => {
      setNotificationFocus(null);
      const url = new URL(window.location.href);
      ['highlight', 'change', 'fields'].forEach(key => url.searchParams.delete(key));
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }, 4500);
    return () => window.clearTimeout(clearTimer);
  }, [notificationFocus, selectedDateId]);

  // 1. Firebase에서 실시간 데이터 불러오기
  useEffect(() => {
    const academyConfig = getAcademyConfig(academy);
    if (isTestMode) {
      setSchedules([]);
      setLocations([]);
      setUniforms([]);
      setIsLoading(true);
      scheduleDatabaseKeyByDateRef.current.clear();
      let controller = new AbortController();
      let disposed = false;
      let loading = false;
      const load = async () => {
        if (loading || disposed) return;
        controller = new AbortController();
        loading = true;
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
          if (!isStagingSession()) throw new Error('Staging session required.');
          const token = await getAdminIdToken();
          if (!token) throw new Error('Sign out and sign in to the test environment again.');
          const values = await Promise.all(['schedules', 'locations', 'uniforms'].map(async name => {
            const response = await fetch(stagingDatabaseUrl(`${academyConfig.databasePrefix}${name}`, token), {
              cache: 'no-store', signal: controller.signal
            });
            if (!response.ok) {
              const reason = response.status === 401 || response.status === 403
                ? 'Test database rejected access. Check staging rules and sign in again.'
                : response.status === 404
                  ? 'Staging database endpoint was not found. Check the staging project setup.'
                  : `Test database request failed (${response.status}).`;
              throw new Error(reason);
            }
            return response.json();
          }));
          if (controller.signal.aborted) return;
          const entries = Object.entries((values[0] || {}) as Record<string, DailySchedule>)
            .filter(([, day]) => Boolean(day?.date));
          recordScheduleKeys(entries);
          setSchedules(entries.map(([, day]) => ({ ...day, events: normalizeScheduleEvents(day.events) }))
            .sort((a, b) => a.date.localeCompare(b.date)));
          setLocations(values[1] ? Object.values(values[1]) as string[] : academyConfig.defaultLocations);
          setUniforms(values[2] ? Object.values(values[2]) as string[] : academyConfig.defaultUniforms);
          setApiError(null);
        } catch (error) {
          if (!disposed) {
            setApiError(error instanceof Error
              ? error.message
              : 'Cannot load the test database. Check the staging deployment or sign in again.');
          }
        } finally {
          window.clearTimeout(timeout);
          loading = false;
          if (!disposed) setIsLoading(false);
        }
      };
      void load();
      const timer = window.setInterval(() => void load(), 5000);
      const refresh = () => { void load(); };
      window.addEventListener('staging-data-changed', refresh);
      return () => { disposed = true; controller.abort(); window.clearInterval(timer); window.removeEventListener('staging-data-changed', refresh); };
    }
    const databasePrefix = academyConfig.databasePrefix;
    const schedulesPath = `${databasePrefix}schedules`;
    const locationsPath = `${databasePrefix}locations`;
    const uniformsPath = `${databasePrefix}uniforms`;
    const schedulesRef = ref(db, schedulesPath);
    const locationsRef = ref(db, locationsPath);
    const uniformsRef = ref(db, uniformsPath);
    const nativeHttpsSync = Capacitor.isNativePlatform() && !useFirebaseEmulators;
    let receivedSchedules = false;
    let scheduleRevision = 0;
    let httpsLoading = false;
    const loadingTimeout = window.setTimeout(() => {
      if (receivedSchedules) return;
      setApiError('Unable to connect to the schedule database. Check your internet connection and try again.');
      setIsLoading(false);
    }, 15000);

    // Native reads and writes use HTTPS consistently: WebView realtime
    // connections can silently stop delivering changes after app suspension.
    const abortController = new AbortController();
    const fetchDatabaseValue = async (path: string, signal: AbortSignal) => {
      const response = await fetch(getDatabaseRestUrl(path), {
        cache: 'no-store',
        signal
      });
      if (!response.ok) throw new Error(`Database request failed (${response.status})`);
      return response.json();
    };

    const loadViaHttps = () => {
      if (httpsLoading || abortController.signal.aborted) return;
      httpsLoading = true;
      const revision = scheduleRevision;
      const requestController = new AbortController();
      const abortRequest = () => requestController.abort();
      abortController.signal.addEventListener('abort', abortRequest);
      const requestTimeout = window.setTimeout(abortRequest, 15000);
      void Promise.all([
        fetchDatabaseValue(schedulesPath, requestController.signal),
        fetchDatabaseValue(locationsPath, requestController.signal).catch(() => null),
        fetchDatabaseValue(uniformsPath, requestController.signal).catch(() => null)
      ]).then(([scheduleData, locationData, uniformData]) => {
        if (abortController.signal.aborted || requestController.signal.aborted || revision !== scheduleRevision || (!nativeHttpsSync && receivedSchedules)) return;
        const rawEntries = scheduleData && typeof scheduleData === 'object'
          ? Object.entries(scheduleData as Record<string, DailySchedule>)
          : academy === 'BLC' && useFirebaseEmulators
            ? mockSchedules.map((schedule, index) => [String(index), schedule] as const)
            : [];
        recordScheduleKeys(rawEntries.map(([key, day]) => [key, day]));
        const rawSchedules = rawEntries.map(([, day]) => day);
        const initialSchedules = rawSchedules
          .filter((day): day is DailySchedule => Boolean(day && typeof day === 'object' && day.date))
          .map(day => ({
            ...day,
            notes: day.notes || '',
            notesHighlighted: Boolean(day.notesHighlighted),
            sglNotes: day.sglNotes || '',
            sglNotesHighlighted: Boolean(day.sglNotesHighlighted),
            events: normalizeScheduleEvents(day.events)
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

        window.clearTimeout(loadingTimeout);
        setSchedules(initialSchedules);
        if (locationData || !receivedSchedules) setLocations(locationData
          ? (Array.isArray(locationData) ? locationData : Object.values(locationData)) as string[]
          : academyConfig.defaultLocations);
        if (uniformData || !receivedSchedules) setUniforms(uniformData
          ? (Array.isArray(uniformData) ? uniformData : Object.values(uniformData)) as string[]
          : academyConfig.defaultUniforms);
        receivedSchedules = true;
        setApiError(null);
        setIsLoading(false);
      }).catch(error => {
        if (abortController.signal.aborted) return;
        console.error('Initial database HTTPS load failed:', error);
        if (!receivedSchedules) {
          window.clearTimeout(loadingTimeout);
          setApiError('Unable to load schedules. Check your connection and try again.');
          setIsLoading(false);
        } else if (nativeHttpsSync) {
          setApiError('Schedule refresh failed. Showing the last loaded schedule; changes may be missing. Reconnect to refresh.');
        }
      }).finally(() => {
        httpsLoading = false;
        window.clearTimeout(requestTimeout);
        abortController.signal.removeEventListener('abort', abortRequest);
        if (revision !== scheduleRevision && !abortController.signal.aborted) loadViaHttps();
      });
    };
    loadViaHttps();
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') loadViaHttps();
    };
    const confirmedWrite = () => { scheduleRevision++; };
    const uncertainWrite = () => { scheduleRevision++; refreshVisible(); };
    const refreshTimer = nativeHttpsSync ? window.setInterval(refreshVisible, 30000) : undefined;
    if (nativeHttpsSync) {
      document.addEventListener('visibilitychange', refreshVisible);
      window.addEventListener('online', refreshVisible);
      window.addEventListener('blc-schedule-notification', refreshVisible);
      window.addEventListener('schedule-write-confirmed', confirmedWrite);
      window.addEventListener('schedule-write-uncertain', uncertainWrite);
    }

    // 사이클 제목 감시
    // 스케줄 감시
    const unsubSchedules = nativeHttpsSync ? () => undefined : onValue(schedulesRef, (snapshot) => {
      if (abortController.signal.aborted) return;
      receivedSchedules = true;
      window.clearTimeout(loadingTimeout);
      setApiError(null);

      const data = snapshot.val();
      if (data) {
        const scheduleEntries = Object.entries(data as Record<string, DailySchedule>)
          .filter((entry): entry is [string, DailySchedule] => Boolean(entry[1]?.date));
        recordScheduleKeys(scheduleEntries);
        let schedulesArray = scheduleEntries.map(([, day]) => day);
        
        let normalizedLegacyCycle = false;

        // Ensure every day has an events array (Firebase omits empty arrays)
        schedulesArray = schedulesArray.map(day => {
          const isLegacy0626Date = academy === 'BLC' && day.date >= LEGACY_06_26_START && day.date <= LEGACY_06_26_END;
          const missingCycleName = !day.cycleName || String(day.cycleName).trim() === '';

          if (isLegacy0626Date && missingCycleName) {
            normalizedLegacyCycle = true;
            return {
              ...day,
              cycleName: LEGACY_06_26_CYCLE,
              notes: day.notes || "",
              notesHighlighted: Boolean(day.notesHighlighted),
              sglNotes: day.sglNotes || "",
              sglNotesHighlighted: Boolean(day.sglNotesHighlighted),
              events: normalizeScheduleEvents(day.events)
            };
          }

          return {
            ...day,
            notes: day.notes || "",
            notesHighlighted: Boolean(day.notesHighlighted),
            sglNotes: day.sglNotes || "",
            sglNotesHighlighted: Boolean(day.sglNotesHighlighted),
            events: normalizeScheduleEvents(day.events)
          };
        });

        // Sort by date to ensure correct order
        schedulesArray.sort((a, b) => a.date.localeCompare(b.date));

        setSchedules(schedulesArray as DailySchedule[]);
        if (normalizedLegacyCycle && useFirebaseEmulators) {
          set(schedulesRef, schedulesArray).catch(err => {
            console.error("Error normalizing 06-26 cycleName:", err);
          });
        }
        setIsLoading(false);
      } else {

        if (useFirebaseEmulators && academy === 'BLC') {
          set(schedulesRef, mockSchedules)
            .then(() => setIsLoading(false))
            .catch(err => {
              console.error("Error setting initial schedules:", err);
              setApiError("Failed to initialize schedules: " + err.message);
              setIsLoading(false);
            });
        } else {
          setSchedules([]);
          setIsLoading(false);
        }
      }
    }, (error) => {
      if (abortController.signal.aborted) return;
      console.error("Schedules sync error:", error);
      if (!receivedSchedules) {
        setApiError('Unable to sync schedules. Checking the database connection...');
        setIsLoading(false);
      }
    });

    // 위치 데이터 감시
    const unsubLocations = nativeHttpsSync ? () => undefined : onValue(locationsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const locsArray = Array.isArray(data) ? data : Object.values(data);
        setLocations(locsArray as string[]);
      } else {
        setLocations(academyConfig.defaultLocations);
      }
    });

    // 복장 데이터 감시
    const unsubUniforms = nativeHttpsSync ? () => undefined : onValue(uniformsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const unisArray = Array.isArray(data) ? data : Object.values(data);
        setUniforms(unisArray as string[]);
      } else {
        setUniforms(academyConfig.defaultUniforms);
      }
    }, (error) => {
      console.error("Uniforms sync error:", error);
    });

    return () => {
      abortController.abort();
      window.clearTimeout(loadingTimeout);
      window.clearInterval(refreshTimer);
      document.removeEventListener('visibilitychange', refreshVisible);
      window.removeEventListener('online', refreshVisible);
      window.removeEventListener('blc-schedule-notification', refreshVisible);
      window.removeEventListener('schedule-write-confirmed', confirmedWrite);
      window.removeEventListener('schedule-write-uncertain', uncertainWrite);
      unsubSchedules();
      unsubLocations();
      unsubUniforms();
    };
  }, [isTestMode, academy, role]);

  useEffect(() => {
    if (role || schedules.length === 0 || typeof window === 'undefined') return;

    try {
      const saved = window.localStorage.getItem(LOGIN_STORAGE_KEY);
      if (!saved) return;

      const parsed = JSON.parse(saved) as SavedLogin | null;
      if (!Capacitor.isNativePlatform() && parsed?.testMode !== true && !canUseWebsite(parsed?.profile || null)) {
        window.localStorage.removeItem(LOGIN_STORAGE_KEY);
        return;
      }
      if (parsed?.role === 'ADMIN') return;
      if (!parsed?.code) return;

      const login = resolveAccessCode(parsed.code, schedules, academy);
      if (!login?.role || (!Capacitor.isNativePlatform() && !canUseWebsite(login))) {
        window.localStorage.removeItem(LOGIN_STORAGE_KEY);
        return;
      }

      setRole(login.role);
      setAccessProfile(login);
      setAcademy(login.academy);
      setStudentCycleName(login.studentCycleName || null);
      hasAutoSelectedTodayRef.current = false;
    } catch {
      window.localStorage.removeItem(LOGIN_STORAGE_KEY);
    }
  }, [role, schedules, academy]);

  // 로그인 시 오늘 날짜 자동 선택
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSetRole = (newRole: UserRole) => {
    setRole(newRole);
    hasAutoSelectedTodayRef.current = false;
    if (newRole) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      
      // schedules가 로딩된 후에만 체크 가능하지만, 이미 로딩된 상태일 가능성이 높음
      if (schedules.length > 0) {
        const hasSchedule = schedules.some(s => s.date === todayStr);
        if (hasSchedule) {
          hasAutoSelectedTodayRef.current = true;
          setSelectedDateId(todayStr);
        }
      }
    }
  };

  // 사이클 제목 수정 함수
  // 새로운 위치 추가 함수 (Firebase에 직접 반영)
  const addLocation = (loc: string) => {
    if (loc && !locations.includes(loc)) {
      setDatabaseValue(getDatabasePath('locations'), [...locations, loc], isTestMode)
      .then(() => setLocations(current => current.includes(loc) ? current : [...current, loc])).catch(err => {
        alert("Failed to add location: " + err.message);
      });
    }
  };

  // 새로운 복장 추가 함수 (Firebase에 직접 반영)
  const addUniform = (uni: string) => {
    if (uni && !uniforms.includes(uni)) {
      setDatabaseValue(getDatabasePath('uniforms'), [...uniforms, uni], isTestMode)
      .then(() => setUniforms(current => current.includes(uni) ? current : [...current, uni])).catch(err => {
        alert("Failed to add uniform: " + err.message);
      });
    }
  };

  const queueScheduleNotification = (
    dateStr: string,
    changeType: string,
    targetId: string,
    changedFields: string[] = [],
    previewText?: string,
    eventName?: string
  ) => {
    const changedDay = schedules.find(day => day.date === dateStr);
    setPendingNotification({
      notificationId: `${Date.now()}-${Array.from(window.crypto.getRandomValues(new Uint32Array(4)), value => value.toString(16)).join('')}`,
      eventName,
      date: dateStr,
      cycleName: changedDay?.cycleName || null,
      changeType,
      previewText,
      targetId,
      changedFields
    });
  };

  const updateScheduleLocally = (
    dateStr: string,
    updater: (schedule: DailySchedule) => DailySchedule
  ) => {
    setSchedules(current => current.map(schedule =>
      schedule.date === dateStr ? updater(schedule) : schedule
    ));
  };

  // 관리자가 이벤트를 수정했을 때 호출되는 함수 (Firebase 업데이트)
  const handleDismissKtaConflict = (dateStr: string, conflictKey: string) => {
    const profile = accessProfile;
    if (
      profile?.academy !== 'KTA'
      || profile.scope !== 'KTA'
      || profile.accessLevel !== 'SCHEDULE_IMPORTER'
      || !hasPermission(profile, 'schedule.write')
    ) return;

    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex < 0 || !dayKey) return;

    const existing = schedules[dayIndex].dismissedConflictKeys || [];
    if (existing.includes(conflictKey)) return;
    const dismissedConflictKeys = [...existing, conflictKey];
    const updates: Record<string, string[]> = {};
    updates[getScheduleUpdatePath(`schedules/${dayKey}/dismissedConflictKeys`)] = dismissedConflictKeys;
    updateDatabaseValues(updates, isTestMode)
      .then(() => updateScheduleLocally(dateStr, schedule => ({ ...schedule, dismissedConflictKeys })))
      .catch(error => alert(`Failed to dismiss KTA schedule conflict: ${error.message}`));
  };

  const handleSaveEvent = (dateStr: string, updatedEvent: TrainingEvent) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex !== -1 && dayKey !== undefined) {
      const currentEvents = schedules[dayIndex].events || [];
      const eventIndex = currentEvents.findIndex(ev => ev.id === updatedEvent.id);
      const eventKey = eventDatabaseKeysRef.current.get(dateStr)?.get(updatedEvent.id);
      if (eventIndex !== -1 && eventKey !== undefined) {
        const originalEvent = currentEvents[eventIndex];
        const changedFields = (['time', 'eventName', 'location', 'uniform', 'highlighted'] as const)
          .filter(field => originalEvent[field] !== updatedEvent[field]);
        const updates: any = {};
        updates[getScheduleUpdatePath(`schedules/${dayKey}/events/${eventKey}`)] = updatedEvent;
        return updateDatabaseValues(updates, isTestMode)
          .then(() => {
            updateScheduleLocally(dateStr, day => ({
              ...day, events: day.events.map(event => event.id === updatedEvent.id ? updatedEvent : event)
            }));
            queueScheduleNotification(
              dateStr,
              'Event updated',
              `event:${updatedEvent.id}`,
              changedFields,
              buildEventPreview(updatedEvent, changedFields),
              updatedEvent.eventName
            );
            return true;
          })
          .catch(err => {
            alert("Failed to save changes: " + err.message);
            return false;
          });
      }
    }
    return Promise.resolve(false);
  };

  const handleSaveDayNotes = (dateStr: string, notes: string) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex !== -1 && dayKey !== undefined) {
      const updates: any = {};
      updates[getScheduleUpdatePath(`schedules/${dayKey}/notes`)] = notes.trim();
      updateDatabaseValues(updates, isTestMode)
        .then(() => {
          updateScheduleLocally(dateStr, day => ({ ...day, notes: notes.trim() }));
          queueScheduleNotification(
            dateStr,
            'Notes updated',
            'notes',
            ['notes'],
            `NOTE: ${truncateNotificationPreview(notes.trim() || 'Notes cleared')}`
          );
        })
        .catch(err => {
          alert("Failed to save notes: " + err.message);
        });
    }
  };

  const handleSaveDayLabel = (dateStr: string, dayLabel: string) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    const normalizedLabel = dayLabel.trim().replace(/\s+/g, ' ').toUpperCase();
    if (dayIndex === -1 || dayKey === undefined || !normalizedLabel) return;

    const updates: any = {};
    updates[getScheduleUpdatePath(`schedules/${dayKey}/dayLabel`)] = normalizedLabel;
    updateDatabaseValues(updates, isTestMode)
      .then(() => {
        updateScheduleLocally(dateStr, day => ({ ...day, dayLabel: normalizedLabel }));
        queueScheduleNotification(dateStr, 'Day number updated', 'day', ['dayLabel'], `DAY LABEL: ${normalizedLabel}`);
      })
      .catch(err => alert("Failed to update day number: " + err.message));
  };

  const handleToggleDayNotesHighlight = (dateStr: string) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex !== -1 && dayKey !== undefined) {
      const updates: any = {};
      updates[getScheduleUpdatePath(`schedules/${dayKey}/notesHighlighted`)] = !schedules[dayIndex].notesHighlighted;
      updateDatabaseValues(updates, isTestMode)
        .then(() => {
          const highlighted = !schedules[dayIndex].notesHighlighted;
          updateScheduleLocally(dateStr, day => ({ ...day, notesHighlighted: highlighted }));
          queueScheduleNotification(
            dateStr,
            'Notes highlight changed',
            'notes',
            ['highlighted'],
            `NOTE HIGHLIGHT: ${highlighted ? 'ON' : 'OFF'}`
          );
        })
        .catch(err => {
          alert("Failed to highlight notes: " + err.message);
        });
    }
  };

  const handleSaveSglNotes = (dateStr: string, notes: string) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex !== -1 && dayKey !== undefined) {
      const updates: any = {};
      updates[getScheduleUpdatePath(`schedules/${dayKey}/sglNotes`)] = notes.trim();
      updateDatabaseValues(updates, isTestMode)
        .then(() => {
          updateScheduleLocally(dateStr, day => ({ ...day, sglNotes: notes.trim() }));
          queueScheduleNotification(
            dateStr,
            'SGL notes updated',
            'sglNotes',
            ['sglNotes'],
            `SGL NOTE: ${truncateNotificationPreview(notes.trim() || 'SGL notes cleared')}`
          );
        })
        .catch(err => {
          alert("Failed to save SGL notes: " + err.message);
        });
    }
  };

  const handleToggleSglNotesHighlight = (dateStr: string) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex !== -1 && dayKey !== undefined) {
      const updates: any = {};
      updates[getScheduleUpdatePath(`schedules/${dayKey}/sglNotesHighlighted`)] = !schedules[dayIndex].sglNotesHighlighted;
      updateDatabaseValues(updates, isTestMode)
        .then(() => {
          const highlighted = !schedules[dayIndex].sglNotesHighlighted;
          updateScheduleLocally(dateStr, day => ({ ...day, sglNotesHighlighted: highlighted }));
          queueScheduleNotification(
            dateStr,
            'SGL notes highlight changed',
            'sglNotes',
            ['highlighted'],
            `SGL NOTE HIGHLIGHT: ${highlighted ? 'ON' : 'OFF'}`
          );
        })
        .catch(err => {
          alert("Failed to highlight SGL notes: " + err.message);
        });
    }
  };

  // 새로운 이벤트 추가 함수
  const handleCreateEvent = (dateStr: string, newEvent: TrainingEvent) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex !== -1 && dayKey !== undefined) {
      const currentEvents = schedules[dayIndex].events || [];
      const updates: any = {};
      updates[getScheduleUpdatePath(`schedules/${dayKey}/events`)] = [...currentEvents, newEvent];
      return updateDatabaseValues(updates, isTestMode)
        .then(() => {
          eventDatabaseKeysRef.current.set(dateStr, new Map([...currentEvents, newEvent].map((event, index) => [event.id, String(index)])));
          updateScheduleLocally(dateStr, day => ({ ...day, events: [...currentEvents, newEvent] }));
          queueScheduleNotification(
            dateStr,
            'Event added',
            `event:${newEvent.id}`,
            ['eventName', 'time', 'location', 'uniform'],
            buildEventPreview(newEvent, ['time', 'eventName', 'location', 'uniform']),
            newEvent.eventName
          );
          return true;
        })
        .catch(err => {
          alert("Failed to add event: " + err.message);
          return false;
        });
    }
    return Promise.resolve(false);
  };

  // 이벤트 삭제 함수
  const handleDeleteEvent = (dateStr: string, eventId: string) => {
    const dayIndex = schedules.findIndex(day => day.date === dateStr);
    const dayKey = getScheduleDatabaseKey(dateStr);
    if (dayIndex !== -1 && dayKey !== undefined) {
      const currentEvents = schedules[dayIndex].events || [];
      const updatedEvents = currentEvents.filter(ev => ev.id !== eventId);
      const updates: any = {};
      updates[getScheduleUpdatePath(`schedules/${dayKey}/events`)] = updatedEvents;
      return updateDatabaseValues(updates, isTestMode)
        .then(() => {
          eventDatabaseKeysRef.current.set(dateStr, new Map(updatedEvents.map((event, index) => [event.id, String(index)])));
          updateScheduleLocally(dateStr, day => ({ ...day, events: updatedEvents }));
          const deletedEvent = currentEvents.find(ev => ev.id === eventId);
          queueScheduleNotification(
            dateStr,
            'Event deleted',
            'day',
            [],
            deletedEvent ? `DELETED: ${truncateNotificationPreview(deletedEvent.eventName)}` : 'Event deleted',
            deletedEvent?.eventName
          );
          return true;
        })
        .catch(err => {
          alert("Failed to delete event: " + err.message);
          return false;
        });
    }
    return Promise.resolve(false);
  };

  // 스케줄 대량 임포트 함수
  const handleImportSchedules = (newSchedules: DailySchedule[]) => {
    let updatedSchedules = [...schedules];
    
    newSchedules.forEach(newDay => {
      const existingIndex = updatedSchedules.findIndex(s => s.date === newDay.date);
      if (existingIndex !== -1) {
        if (newDay.academy === 'KTA') {
          // A corrected KTA PDF import must replace the previously parsed day.
          // Appending kept stale parser results (such as ROOM CHECK-only duty)
          // alongside the corrected merged ALL HANDS events.
          updatedSchedules[existingIndex] = {
            ...updatedSchedules[existingIndex],
            ...newDay,
            notes: newDay.notes || updatedSchedules[existingIndex].notes,
            notesHighlighted: Boolean(newDay.notesHighlighted || updatedSchedules[existingIndex].notesHighlighted),
            events: newDay.events
          };
        } else {
          updatedSchedules[existingIndex] = {
            ...updatedSchedules[existingIndex],
            notes: [updatedSchedules[existingIndex].notes, newDay.notes].filter(Boolean).join('\n'),
            notesHighlighted: Boolean(updatedSchedules[existingIndex].notesHighlighted || newDay.notesHighlighted),
            sglNotes: [updatedSchedules[existingIndex].sglNotes, newDay.sglNotes].filter(Boolean).join('\n'),
            sglNotesHighlighted: Boolean(updatedSchedules[existingIndex].sglNotesHighlighted || newDay.sglNotesHighlighted),
            events: [...(updatedSchedules[existingIndex].events || []), ...newDay.events]
          };
        }
      } else {
        updatedSchedules.push(newDay);
      }
    });

    updatedSchedules.sort((a, b) => a.date.localeCompare(b.date));

    setDatabaseValue(getDatabasePath('schedules'), updatedSchedules, isTestMode)
      .then(() => {
        recordScheduleKeys(updatedSchedules.map((day, index) => [String(index), day]));
        setSchedules(updatedSchedules);
      })
      .catch(err => {
        alert("Failed to import schedules: " + err.message);
      });
  };

  // 스케줄 초기화 함수 (전체 삭제)
  const handleResetSchedules = () => {
    if (window.confirm("Are you sure you want to CLEAR ALL schedules from the database?")) {
      removeDatabaseValue(getDatabasePath('schedules'), isTestMode).then(() => {
        recordScheduleKeys([]);
        setSchedules([]);
      }).catch(err => {
        alert("Failed to reset schedules: " + err.message);
      });
    }
  };

  // 특정 기수(Cycle) 삭제 함수
  const handleDeleteCycle = (targetCycle: string) => {
    if (window.confirm(`Are you sure you want to delete ALL schedules for cycle [${targetCycle}]?`)) {
      const updatedSchedules = schedules.filter(s => s.cycleName !== targetCycle);
      setDatabaseValue(getDatabasePath('schedules'), updatedSchedules, isTestMode)
        .then(() => {
          recordScheduleKeys(updatedSchedules.map((day, index) => [String(index), day]));
          setSchedules(updatedSchedules);
        })
        .catch(err => {
          alert("Failed to delete cycle: " + err.message);
        });
    }
  };
  const handleLogout = () => {
    setHistoryCredential(null);
    const cleanup = disableNotifications(role, studentCycleName, false, !isTestMode, academy, accessProfile?.accessLevel === 'NCOA_MANAGER');

    // Clear the local session first so slow notification/auth requests cannot
    // leave the user stuck on the calendar after pressing LOGOUT.
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(LOGIN_STORAGE_KEY);
      clearAdminSessionToken();
      window.dispatchEvent(new Event('ncoa-test-session-changed'));
    }

    selectStagingSession(false);
    setSchedules([]);
    setLocations([]);
    setUniforms([]);
    setPendingNotification(null);
    setForegroundNotification(null);
    setNotificationFocus(null);
    setNotificationOnboardingComplete(window.localStorage.getItem('blc_notification_onboarding_dismissed') === 'true');
    setRole(null);
    setTvCycle(null);
    setAccessProfile(null);
    setIsTestMode(false);
    setSelectedDateId(null);
    setStudentCycleName(null);
    hasAutoSelectedTodayRef.current = false;

    // Native sessions use REST tokens cleared above, not Firebase JS Auth.
    // Signing out an unused SDK instance can wait indefinitely for persistence.
    const sdkSessions = (!Capacitor.isNativePlatform() || useFirebaseEmulators)
      ? [auth, stagingAuth].filter(session => session.currentUser)
      : [];
    logoutCleanupRef.current = Promise.allSettled([
      cleanup,
      ...sdkSessions.map(session => signOut(session))
    ]).then(results => {
      const [notificationResult, ...authResults] = results;
      if (notificationResult.status === 'rejected') {
        console.error('Failed to unsubscribe from schedule notifications:', notificationResult.reason);
      }
      if (authResults.some(result => result.status === 'rejected')) {
        console.error('Failed to end administrator session');
      }
    });
  };
  const handleLogin = async (code: string, rememberLogin: boolean) => {
    await waitForLogoutCleanup(logoutCleanupRef.current);
    if (useFirebaseEmulators) {
      await signInAnonymously(auth);
      const emulatorProfile: AccessProfile = {
        role: 'ADMIN', accessLevel: 'NCOA_MANAGER', academy, scope: 'NCOA',
        permissions: ['schedule.read', 'schedule.write', 'schedule.import', 'location.manage', 'conflict.resolve']
      };
      setRole('ADMIN');
      setAccessProfile(emulatorProfile);
      setStudentCycleName(null);
      setSelectedDateId(null);
      setIsTestMode(false);
      hasAutoSelectedTodayRef.current = false;
      if (rememberLogin) {
        window.localStorage.setItem(LOGIN_STORAGE_KEY, JSON.stringify({
          role: 'ADMIN', profile: emulatorProfile, localEmulator: true
        }));
      }
      window.dispatchEvent(new Event('ncoa-test-session-changed'));
      return true;
    }
    const requestedTestMode = code.trim() === '318709';
    const normalizedCode = normalizeAccessCode(code);
    const isWeb = !Capacitor.isNativePlatform();
    const webProfile = resolveAccessCode(code, schedules, academy);
    if (isWeb && webProfile && !canUseWebsite(webProfile) && !requestedTestMode) return false;

    // Preserve the released BLC SGL login exactly as it worked before the
    // NCOA/KTA access-code extensions. Legacy codes must never depend on the
    // new academy resolver or server authentication path.
    if (normalizedCode === '9876') {
      try { await prepareNotificationEnvironment(false); } catch { return false; }
      const legacyProfile: AccessProfile = {
        role: 'VIEWER',
        accessLevel: 'SENIOR',
        academy: 'BLC',
        scope: 'BLC',
        permissions: ['schedule.read']
      };
      hasAutoSelectedTodayRef.current = false;
      setRole('VIEWER');
      setAccessProfile(legacyProfile);
      setHistoryCredential(normalizedCode);
      setAcademy('BLC');
      setStudentCycleName(null);
      if (typeof window !== 'undefined') {
        if (rememberLogin) {
          window.localStorage.setItem(LOGIN_STORAGE_KEY, JSON.stringify({
            code: '9876',
            role: 'VIEWER',
            profile: legacyProfile
          }));
        } else {
          window.localStorage.removeItem(LOGIN_STORAGE_KEY);
        }
      }
      return true;
    }

    const inferredAcademy: AcademyId = normalizedCode.startsWith('KTA') ? 'KTA' : 'BLC';
    let schedulesForLogin = schedules;

    if (inferredAcademy !== academy && /^(BLC|KTA)\d{4}$/.test(normalizedCode)) {
      try {
        const path = `${getAcademyConfig(inferredAcademy).databasePrefix}schedules`;
        const response = await fetch(getDatabaseRestUrl(path), { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          schedulesForLogin = data && typeof data === 'object'
            ? Object.values(data as Record<string, DailySchedule>)
            : [];
        }
      } catch {
        return false;
      }
    }

    let login = resolveAccessCode(code, schedulesForLogin, academy);
    if (isWeb && !login && !requestedTestMode) return false;

    if (login?.requiresServerAuth) {
      try {
        selectStagingSession(false);
        await createAdminSession(normalizedCode);
      } catch {
        return false;
      }
    } else if (!login?.role) {
      // Keep the existing test administrator flow available during migration.
      try {
        selectStagingSession(requestedTestMode);
        await createAdminSession(code.trim());
        login = {
          role: 'ADMIN',
          accessLevel: 'NCOA_MANAGER',
          academy: 'BLC',
          scope: 'NCOA',
          permissions: ['schedule.read', 'schedule.write', 'schedule.import', 'location.manage', 'conflict.resolve']
        };
      } catch {
        clearAdminSessionToken();
        selectStagingSession(false);
        return false;
      }
    }

    if (isWeb && !canUseWebsite(login) && !requestedTestMode) return false;
    try { await prepareNotificationEnvironment(requestedTestMode); } catch {
      clearAdminSessionToken();
      selectStagingSession(false);
      return false;
    }
    hasAutoSelectedTodayRef.current = false;
    setTvCycle(null);
    setSelectedDateId(null);
    if (login.academy !== academy || requestedTestMode !== isTestMode) {
      setSchedules([]);
      setIsLoading(true);
    }
    setRole(login.role);
    setAccessProfile(login);
    setAcademy(login.academy);
    setHistoryCredential(requestedTestMode ? null : normalizedCode);
    setIsTestMode(requestedTestMode);
    setNotificationOnboardingComplete(window.localStorage.getItem('blc_notification_onboarding_dismissed') === 'true');
    setPendingNotification(null);
    setNotificationFocus(null);
    setForegroundNotification(null);
    if (requestedTestMode) {
      setDisplayMode('auto');
      setTestViewId('test-manager');
    }
    setStudentCycleName(login.studentCycleName || null);

    if (typeof window !== 'undefined') {
      if (rememberLogin || requestedTestMode) {
        window.localStorage.setItem(
          LOGIN_STORAGE_KEY,
          JSON.stringify({
            code: normalizedCode,
            role: login.role,
            testMode: requestedTestMode,
            profile: login
          })
        );
      } else {
        window.localStorage.removeItem(LOGIN_STORAGE_KEY);
      }
    }

    if (typeof window !== 'undefined') window.dispatchEvent(new Event('ncoa-test-session-changed'));
    return true;
  };

  const handleBackToCalendar = () => {
    if (isTvDisplay) return;
    hasAutoSelectedTodayRef.current = true;
    setSelectedDateId(null);
  };

  useEffect(() => {
    if (featureTour.pending) tourShownThisSession.current = true;
    if (tourShownThisSession.current) return;
    if (!role || isTvDisplay || !Capacitor.isNativePlatform()) return;

    let wasVisible = document.visibilityState === 'visible';
    const showIfDue = (isResume = false) => {
      void showAppOpenAdIfEligible(isTestMode, isResume);
    };
    const initialOpenTimer = window.setTimeout(showIfDue, 300);
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      if (isVisible && !wasVisible && !isAppOpenAdShowing()) showIfDue(true);
      wasVisible = isVisible;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearTimeout(initialOpenTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [role, isTvDisplay, isTestMode, featureTour.pending]);

  const handleAcademyChange = (nextAcademy: AcademyId) => {
    if (accessProfile?.scope !== 'NCOA' || nextAcademy === academy) return;
    setAcademy(nextAcademy);
    setAccessProfile({ ...accessProfile, academy: nextAcademy });
    setSelectedDateId(null);
    setStudentCycleName(null);
    setSchedules([]);
    setIsLoading(true);
    hasAutoSelectedTodayRef.current = false;
  };

  const handleTestViewChange = (viewId: TestViewId) => {
    if (!isTestMode) return;
    const profile = createTestAccessProfile(viewId, academy, testStudentCycles);
    if (!profile) return;
    setTestViewId(viewId);
    setRole(profile.role);
    setAccessProfile(profile);
    setAcademy(profile.academy);
    setStudentCycleName(profile.studentCycleName || null);
    setSelectedDateId(null);
    setTvCycle(null);
    setDisplayMode('auto');
    hasAutoSelectedTodayRef.current = false;
  };

  useEffect(() => {
    if (!role || selectedDateId || schedules.length === 0 || hasAutoSelectedTodayRef.current) return;

    const linkedDate = new URLSearchParams(window.location.search).get('date');
    const availableSchedules = role === 'STUDENT'
      ? schedules.filter(schedule => schedule.cycleName === studentCycleName)
      : schedules;

    if (linkedDate && availableSchedules.some(schedule => schedule.date === linkedDate)) {
      hasAutoSelectedTodayRef.current = true;
      setSelectedDateId(linkedDate);
      return;
    }

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    if (schedules.some(s => s.date === todayStr)) {
      hasAutoSelectedTodayRef.current = true;
      setSelectedDateId(todayStr);
    }
  }, [role, schedules, selectedDateId, studentCycleName]);

  const activeCycleName = useMemo(() => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const cycleRanges = new Map<string, { start: string; end: string }>();

    schedules.forEach(schedule => {
      const cycleName = (schedule.cycleName || '').trim();
      if (!cycleName) return;

      const existing = cycleRanges.get(cycleName);
      if (!existing) {
        cycleRanges.set(cycleName, { start: schedule.date, end: schedule.date });
        return;
      }

      if (schedule.date < existing.start) existing.start = schedule.date;
      if (schedule.date > existing.end) existing.end = schedule.date;
    });

    const activeOrNextCycle = Array.from(cycleRanges.entries())
      .sort((a, b) => a[1].start.localeCompare(b[1].start))
      .find(([, range]) => range.end >= todayStr);

    return activeOrNextCycle ? activeOrNextCycle[0] : null;
  }, [schedules]);

  const tvAvailableSchedules = useMemo(() => schedules.filter(schedule =>
    !schedule.academy || schedule.academy === academy
  ), [schedules, academy]);
  const effectiveTvCycle = tvCycle !== null && tvAvailableSchedules.some(schedule => schedule.cycleName === tvCycle)
    ? tvCycle
    : chooseAutoTvSchedule(tvAvailableSchedules, getLocalTodayString(new Date(tvNow)), tvNow, getScheduleEndDateTime)?.cycleName || '';

  const filteredSchedules = useMemo(() => {
    if (isTvDisplay) {
      return tvAvailableSchedules.filter(schedule => schedule.cycleName === effectiveTvCycle)
        .sort((a, b) => a.date.localeCompare(b.date));
    }
    if (role === 'STUDENT') {
      return schedules.filter(s => s.cycleName === studentCycleName);
    }
    return schedules;
  }, [role, schedules, studentCycleName, isTvDisplay, tvAvailableSchedules, effectiveTvCycle]);

  useEffect(() => {
    if (!isTvDisplay) return;
    const nextDate = chooseTvSchedule(filteredSchedules, getLocalTodayString(), selectedDateId)?.date || null;
    if (nextDate !== selectedDateId) setSelectedDateId(nextDate);
  }, [isTvDisplay, filteredSchedules, selectedDateId]);

  useEffect(() => {
    if (!role || !selectedDateId || filteredSchedules.length === 0) return;

    const advanceScheduleIfNeeded = () => {
      if (displayMode !== 'tv') return;

      const todayStr = getLocalTodayString();

      if (selectedDateId < todayStr) {
        const nextSchedule = filteredSchedules.find(schedule => schedule.date >= todayStr);

        if (nextSchedule && nextSchedule.date !== selectedDateId) {
          hasAutoSelectedTodayRef.current = true;
          setSelectedDateId(nextSchedule.date);
        }
        return;
      }

      const currentSchedule = filteredSchedules.find(schedule => schedule.date === selectedDateId);
      const nextSchedule = filteredSchedules.find(schedule => schedule.date > selectedDateId);
      const currentScheduleEnd = getScheduleEndDateTime(currentSchedule);

      if (nextSchedule && currentScheduleEnd && Date.now() > currentScheduleEnd.getTime()) {
        hasAutoSelectedTodayRef.current = true;
        setSelectedDateId(nextSchedule.date);
      }
    };

    advanceScheduleIfNeeded();
    const intervalId = window.setInterval(advanceScheduleIfNeeded, 30000);
    return () => window.clearInterval(intervalId);
  }, [role, selectedDateId, filteredSchedules, displayMode]);

  const cycleTitle = useMemo(() => {
    const titleCycleName = role === 'STUDENT' ? studentCycleName : activeCycleName;
    return titleCycleName ? `${academy} CLASS ${titleCycleName}` : `${academy} CLASS`;
  }, [role, studentCycleName, activeCycleName, academy]);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-blue-900 text-white font-bold">
        Loading Data...
      </div>
    );
  }

  if (apiError && schedules.length === 0) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-red-900 text-white p-6 text-center">
        <h1 className="text-2xl font-bold mb-4">API Connection Error</h1>
        <p className="mb-6">{apiError}</p>
        <button 
          onClick={() => window.location.reload()}
          className="bg-white text-red-900 px-6 py-2 rounded-lg font-bold"
        >
          RETRY
        </button>
        {isTestMode && (
          <button
            onClick={handleLogout}
            className="mt-3 rounded-lg border border-white px-5 py-2 font-bold text-white"
          >
            SIGN OUT &amp; SIGN IN AGAIN
          </button>
        )}
      </div>
    );
  }

  if (!role) {
    return <Login onLogin={handleLogin} webOnly={!Capacitor.isNativePlatform()} emulatorMode={useFirebaseEmulators} />;
  }

  const scheduleSyncWarning = apiError ? (
    <div role="status" className="rounded-xl border border-amber-500 bg-amber-50 p-3 text-sm text-amber-950">
      {apiError}
      <button className="ml-3 font-bold underline" onClick={() => window.dispatchEvent(new Event('online'))}>Refresh</button>
    </div>
  ) : null;

  const foregroundNotificationToast = foregroundNotification ? (
    <div className="fixed left-3 right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] z-[80] rounded-2xl border-2 border-blue-300 bg-white shadow-2xl ring-4 ring-blue-100 sm:left-auto sm:right-4 sm:w-96">
    <button type="button" aria-label="Dismiss schedule notification" onClick={() => setForegroundNotification(null)}
      className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full text-2xl text-gray-700 hover:bg-gray-100">
      <span aria-hidden="true">×</span>
    </button>
    <button
      type="button"
      onClick={() => {
        hasAutoSelectedTodayRef.current = true;
        setSelectedDateId(foregroundNotification.date);
        setNotificationFocus(null);
        window.setTimeout(() => {
          setNotificationFocus({
            date: foregroundNotification.date,
            targetId: foregroundNotification.targetId,
            changeType: foregroundNotification.changeType,
            previewText: foregroundNotification.previewText,
            changedFields: foregroundNotification.changedFields
          });
        }, 0);
        setForegroundNotification(null);
      }}
      className="w-full rounded-2xl p-4 pr-14 text-left"
    >
      <div className="text-[10px] font-black uppercase tracking-widest text-blue-600">
        Schedule notification received
      </div>
      <div className="mt-1 text-sm font-black text-gray-900">
        {foregroundNotification.previewText || foregroundNotification.changeType || 'Schedule updated'}
      </div>
      <div className="mt-1 text-xs font-bold text-gray-500">
        {foregroundNotification.date} · Tap to view highlighted change
      </div>
    </button>
    </div>
  ) : null;
  const featureTourModal = featureTour.open && accessProfile ? (
    <FeatureTour key={`${accessProfile.accessLevel}-${academy}`} profile={accessProfile} onClose={featureTour.dismiss} />
  ) : null;
  const notificationOnboarding = !featureTour.pending && !updatePromptOpen && !useFirebaseEmulators && role && isTrackingAuthorizationResolved && !notificationOnboardingComplete && isPhoneDevice() ? (
    <NotificationOnboarding
      role={role}
      academy={academy}
      allAcademies={accessProfile?.accessLevel === 'NCOA_MANAGER'}
      cycleName={role === 'STUDENT' ? studentCycleName : null}
      testMode={isTestMode}
      onDismiss={() => {
        window.localStorage.setItem('blc_notification_onboarding_dismissed', 'true');
        setNotificationOnboardingComplete(true);
      }}
      onEnabled={() => setNotificationOnboardingComplete(true)}
    />
  ) : null;
  const testModeBadge = isTestMode ? (
    <div className="fixed bottom-3 left-1/2 z-[75] -translate-x-1/2 rounded-full border-2 border-amber-300 bg-amber-100 px-4 py-2 text-xs font-black text-amber-900 shadow-xl">
      TEST MODE ? Separate test database ? This device only
    </div>
  ) : null;
  const emulatorBadge = useFirebaseEmulators && role ? (
    <div className="fixed bottom-3 left-1/2 z-[75] -translate-x-1/2 -translate-y-12 rounded-full border-2 border-amber-300 bg-amber-100 px-4 py-2 text-center text-xs font-black text-amber-950 shadow-xl">
      LOCAL FIREBASE EMULATOR · TEST DATA ONLY · NOTIFICATIONS DISABLED
    </div>
  ) : null;

  const selectedSchedule = isTvDisplay
    ? chooseTvSchedule(filteredSchedules, getLocalTodayString(), selectedDateId)
    : filteredSchedules.find(s => s.date === selectedDateId);
  const renderGeneralSettings = () => role ? (
    <GeneralSettings
      tvDisplay={isTvDisplay}
      role={role}
      academy={academy}
      allAcademies={accessProfile?.accessLevel === 'NCOA_MANAGER'}
      accessLevel={accessProfile?.accessLevel}
      academyControl={accessProfile?.scope === 'NCOA' ? <AcademySwitcher academy={academy} onChange={handleAcademyChange} /> : undefined}
      cycleName={role === 'STUDENT' ? studentCycleName : null}
      schedules={schedules}
      darkMode={darkMode}
      onDarkModeChange={handleDarkModeChange}
      onLogout={handleLogout}
      onOpenAppGuide={!isTvDisplay && accessProfile ? featureTour.replay : undefined}
      onDeleteCycle={handleDeleteCycle}
      onResetSchedules={handleResetSchedules}
      testMode={isTestMode}
      notificationsDisabled={useFirebaseEmulators}
      testSessionControl={isTestMode ? (
        <TestSessionControls
          activeStudentCycles={testStudentCycles}
          viewId={testViewId}
          onViewChange={handleTestViewChange}
        />
      ) : undefined}
    />
  ) : null;

  const tvControls = isTvDisplay ? (
    <TvScheduleControls
      schedules={tvAvailableSchedules}
      cycle={effectiveTvCycle}
      automatic={tvCycle === null || !tvAvailableSchedules.some(schedule => schedule.cycleName === tvCycle)}
      date={selectedSchedule?.date}
      onCycleChange={cycle => { setTvCycle(cycle); setSelectedDateId(null); }}
      onDateChange={setSelectedDateId}
      settings={renderGeneralSettings()}
    />
  ) : null;

  if (selectedSchedule) {
    const currentIndex = filteredSchedules.indexOf(selectedSchedule);
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < filteredSchedules.length - 1;

    const handlePrev = hasPrev ? () => setSelectedDateId(filteredSchedules[currentIndex - 1].date) : undefined;
    const handleNext = hasNext ? () => setSelectedDateId(filteredSchedules[currentIndex + 1].date) : undefined;

    return (
      <>
      {foregroundNotificationToast}
      {scheduleSyncWarning}
      {featureTourModal}
      {notificationOnboarding}
      {testModeBadge}
      {emulatorBadge}
      <DailyView 
        schedule={{ ...selectedSchedule, academy }}
        crossAcademySchedules={crossAcademySchedules}
        crossAcademyConflictNotices={crossAcademyConflictNotices}
        crossAcademyConflictCheckStatus={crossAcademyConflictCheckStatus}
        showCrossAcademyConflicts={canShowCrossAcademyConflicts(accessProfile?.accessLevel, isTestMode)}
        role={role}
        onBack={isTvDisplay ? undefined : handleBackToCalendar}
        viewControls={tvControls}
        onSave={handleSaveEvent}
        onSaveDayLabel={handleSaveDayLabel}
        onSaveNotes={handleSaveDayNotes}
        onToggleNotesHighlight={handleToggleDayNotesHighlight}
        onSaveSglNotes={handleSaveSglNotes}
        onToggleSglNotesHighlight={handleToggleSglNotesHighlight}
        onCreateEvent={handleCreateEvent}
        onDeleteEvent={handleDeleteEvent}
        canDismissKtaConflicts={Boolean(
          accessProfile?.academy === 'KTA'
          && accessProfile.scope === 'KTA'
          && accessProfile.accessLevel === 'SCHEDULE_IMPORTER'
          && hasPermission(accessProfile, 'schedule.write')
        )}
        onDismissKtaConflict={handleDismissKtaConflict}
        locations={locations}
        uniforms={uniforms}
        onAddLocation={addLocation}
        onAddUniform={addUniform}
        onPrev={handlePrev}
        onNext={handleNext}
        notificationHighlightTarget={notificationFocus?.targetId || null}
        notificationChangeType={notificationFocus?.previewText || notificationFocus?.changeType || null}
        notificationChangedFields={notificationFocus?.changedFields || []}
        testMode={isTestMode}
        displayMode={isTvDisplay ? 'tv' : displayMode}
      />
      {pendingNotification && (
        <ScheduleNotificationModal
          change={pendingNotification}
          academy={academy}
          testMode={isTestMode}
          onClose={() => setPendingNotification(null)}
        />
      )}
      <NcoaChatbot academy={academy} role={role} cycleName={role === 'STUDENT' ? studentCycleName : activeCycleName} />
      </>
    );
  }

  if (isTvDisplay) {
    return (
      <div className="app-safe-screen daily-screen display-mode-tv bg-gray-100 flex flex-col">
        {emulatorBadge}
        {tvControls}
        <div className="flex flex-1 items-center justify-center p-6 text-center font-bold text-gray-600">
          No schedules available for {academy}.
        </div>
        <NcoaChatbot academy={academy} role={role} cycleName={effectiveTvCycle || null} />
      </div>
    );
  }

  return (
    <>
      {foregroundNotificationToast}
      {scheduleSyncWarning}
      {featureTourModal}
      {notificationOnboarding}
      {testModeBadge}
      {emulatorBadge}
      <Calendar 
        schedules={filteredSchedules.map(schedule => ({ ...schedule, academy }))}
        crossAcademySchedules={crossAcademySchedules}
        crossAcademyConflictNotices={crossAcademyConflictNotices}
        crossAcademyConflictCheckStatus={crossAcademyConflictCheckStatus}
        showCrossAcademyConflicts={canShowCrossAcademyConflicts(accessProfile?.accessLevel, isTestMode)}
        academy={academy}
        currentCycleName={role === 'STUDENT' ? studentCycleName : activeCycleName}
        onSelectDate={(date) => setSelectedDateId(date)} 
        onSelectSearchResult={(result: EventSearchResult) => {
          hasAutoSelectedTodayRef.current = true;
          setSelectedDateId(result.date);
          setNotificationFocus(null);
          window.setTimeout(() => setNotificationFocus({
            date: result.date,
            targetId: `event:${result.event.id}`,
            changeType: 'Search result',
            previewText: `Search result: ${result.event.eventName}`,
            changedFields: ['eventName']
          }), 0);
        }}
        role={role}
        cycleTitle={cycleTitle}
        notificationControl={accessProfile && accessProfile.accessLevel !== 'TV_DISPLAY' ? (
          <NotificationCenter profile={accessProfile} testMode={isTestMode || useFirebaseEmulators} onSelect={entry => {
            if (entry.academy !== academy || (role === 'STUDENT' && entry.cycleName !== studentCycleName)) {
              return 'This notification is not available for your current school or cycle.';
            }
            const day = filteredSchedules.find(schedule => schedule.date === entry.date && (!entry.cycleName || schedule.cycleName === entry.cycleName));
            if (!day) return 'This schedule is no longer available. The notification remains here for reference.';
            if (/deleted/i.test(entry.changeType) || (entry.targetId.startsWith('event:') && !day.events.some(event => `event:${event.id}` === entry.targetId))) {
              return 'This event has been deleted or replaced. The notification remains here for reference.';
            }
            hasAutoSelectedTodayRef.current = true;
            setNotificationFocus(entry);
            setSelectedDateId(entry.date);
            return null;
          }} />
        ) : undefined}
        onOpenImport={() => setIsImportModalOpen(true)}
        settingsControl={(
          renderGeneralSettings()
        )}
        showAdBanner={!isImportModalOpen}
        testMode={isTestMode}
        displayMode={displayMode}
      />
      {isImportModalOpen && (
        <AcademyScheduleImportModal
          academy={academy}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportSchedules}
          locations={locations}
          uniforms={uniforms}
        />
      )}
      <NcoaChatbot academy={academy} role={role} cycleName={role === 'STUDENT' ? studentCycleName : activeCycleName} />
    </>
  );
}

function FeatureTourPreview({ initialRole }: { initialRole: FeatureTourPreviewRole }) {
  const profiles: Record<FeatureTourPreviewRole, AccessProfile> = {
    student: { role: 'STUDENT', accessLevel: 'STUDENT', academy: 'BLC', scope: 'BLC', permissions: ['schedule.read'], studentCycleName: 'PREVIEW' },
    chief: { role: 'ADMIN', accessLevel: 'SCHEDULE_IMPORTER', academy: 'BLC', scope: 'BLC', permissions: ['schedule.read', 'schedule.write', 'schedule.import', 'location.manage'] },
    sgl: { role: 'VIEWER', accessLevel: 'SENIOR', academy: 'KTA', scope: 'KTA', permissions: ['schedule.read'] },
    commander: { role: 'VIEWER', accessLevel: 'NCOA_MANAGER', academy: 'BLC', scope: 'NCOA', permissions: ['schedule.read', 'conflict.resolve'] }
  };
  const [role, setRole] = useState(initialRole);
  const [open, setOpen] = useState(true);
  return <main className="min-h-screen bg-[var(--soft-bg,#f2f4f6)] p-3 pt-4 text-[#191f28] sm:p-8">
    <header className="settings-card mx-auto max-w-xl rounded-3xl p-5">
      <div className="text-xs font-black uppercase tracking-widest text-blue-800">Local preview · sample screen</div>
      <h1 className="mt-2 text-2xl font-black">App guide</h1>
      <p className="mt-2 text-sm text-gray-600">This preview uses sample content only. It does not connect to Firebase or edit schedules.</p>
      <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(['student', 'chief', 'sgl', 'commander'] as const).map(option => <button key={option} type="button"
          onClick={() => { setRole(option); setOpen(true); }} className={`min-h-11 rounded-xl px-3 text-xs font-black capitalize ${role === option ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700'}`}>
          {option}
        </button>)}
      </div>
      {!open && <button type="button" onClick={() => setOpen(true)} className="mt-4 min-h-11 w-full rounded-xl bg-blue-900 px-4 text-sm font-bold text-white">Open app guide again</button>}
    </header>
    <div className="settings-card mx-auto mt-5 max-w-xl rounded-3xl p-5 opacity-70" aria-hidden="true">
      <div className="text-xs font-bold text-slate-500">SAMPLE CALENDAR</div>
      <div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs font-semibold text-slate-500">{['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => <span key={index}>{day}</span>)}</div>
      <div className="mt-2 grid grid-cols-7 gap-2">{Array.from({ length: 28 }, (_, index) => <span key={index} className="flex aspect-square items-center justify-center rounded-lg bg-slate-50 text-xs text-slate-500">{index + 1}</span>)}</div>
    </div>
    {open && <FeatureTour key={role} profile={profiles[role]} onClose={() => setOpen(false)} />}
  </main>;
}

function App() {
  const previewRole = getFeatureTourPreviewRole();
  if (previewRole) return <FeatureTourPreview initialRole={previewRole} />;
  return <AppApplication />;
}

export default App;
